/**
 * Kontrollkästchen.
 *
 * Übernommen aus `components/forms/Checkbox.jsx`. Anders als dort liegt hier ein
 * echtes `<input type="checkbox">` unter dem gezeichneten Kästchen — sichtbar
 * nur für Tastatur und Screenreader. Ein `<span>` mit `onClick` sieht gleich aus
 * und ist mit der Tastatur nicht erreichbar; das fällt bei der Anmeldung sofort
 * auf, weil „Angemeldet bleiben" sonst nur mit der Maus zu setzen ist.
 */
import { useId, type ReactNode } from "react";

export interface CheckboxProps {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly label: ReactNode;
  readonly disabled?: boolean;
  readonly id?: string;
  /**
   * Warum der Haken fehlt — unter der Beschriftung, rot und am Feld hängend
   * (`aria-describedby`), wie bei `Input`. Ohne ihn blieb einem Pflichthaken nur
   * ein gesperrter Knopf, der nicht sagt, woran es liegt.
   */
  readonly error?: string;
}

export function Checkbox({ checked, onChange, label, disabled = false, id, error }: CheckboxProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = error !== undefined ? `${inputId}-error` : undefined;

  const box = (
    <label
      htmlFor={inputId}
      style={{
        /**
         * **Notwendig, nicht kosmetisch.** Das `<input>` darunter liegt
         * `absolute`; ohne einen positionierten Vorfahren bezöge es sich auf die
         * Seite und säße irgendwo in der linken oberen Ecke — unsichtbar, aber
         * anklickbar, und dort fängt es Klicks ab, die jemand anderem galten.
         */
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 14,
        color: "var(--text-body)",
        opacity: disabled ? 0.5 : 1,
        // Berührziel: mindestens 44 pt bzw. 48 dp, siehe Design-System.
        minHeight: 44,
      }}
    >
      <input
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={errorId}
        onChange={(event) => onChange(event.target.checked)}
        /**
         * Nicht `display: none` und nicht `visibility: hidden` — beides nimmt
         * das Feld aus der Tabulatorreihenfolge. `opacity: 0` über dem
         * gezeichneten Kästchen lässt es erreichbar und trotzdem unsichtbar.
         */
        style={{
          position: "absolute",
          width: 20,
          height: 20,
          margin: 0,
          opacity: 0,
          cursor: "inherit",
        }}
      />
      <span
        aria-hidden="true"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 20,
          flexShrink: 0,
          borderRadius: "var(--radius-xs)",
          background: checked ? "var(--hm-green-500)" : "var(--surface-card)",
          border: `1.5px solid ${
            checked
              ? "var(--hm-green-500)"
              : error !== undefined
                ? "var(--hm-danger)"
                : "var(--border-strong)"
          }`,
          transition: "background .15s ease, border-color .15s ease",
        }}
      >
        {checked && (
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path
              d="M2.5 6.8L5 9.2L10.5 3.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: "var(--text-invert)" }}
            />
          </svg>
        )}
      </span>
      {label}
    </label>
  );

  if (error === undefined) return box;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {box}
      <span id={errorId} style={{ fontSize: 12, color: "var(--hm-danger)" }}>
        {error}
      </span>
    </div>
  );
}
